import React, { useState, useCallback, useMemo } from 'react'
import { AutoRenewIcon, Button, Card, CardBody, Flex, Skeleton, Text, Link } from 'vvs-uikit'
import styled from 'styled-components'
import BigNumber from 'bignumber.js'
import _ from 'lodash'
import { useTranslation } from 'contexts/Localization'
import Tokens from 'config/constants/tokens'
import { usePriceVvsUsdc } from 'state/farms/hooks'
import { usePools } from 'state/pools/hooks'
import { useRemovalOrders } from 'state/burn/hooks'
import useToast from 'hooks/useToast'
import { useCraftsman, useCraftsmanV2, useSousChef } from 'hooks/useContract'
import { harvestFarm } from 'utils/calls'
import { getBalanceAmount, getBalanceNumber } from 'utils/formatBalance'
import LocalBalance from 'components/LocalBalance'
import useClpHandlers from 'views/RemoveLiquidity/hooks/useClpHandlers'
import useFarmsWithBalance from 'views/Home/hooks/useFarmsWithBalance'
import useFarmsV2WithBalance from 'views/Home/hooks/useFarmsV2WithBalance'
import { harvestPool } from 'views/Pools/hooks/useHarvestPool'
import { useUserVvsBoost } from 'state/xVvs/hooks'
import { BIG_ZERO } from 'utils/bigNumber'
import useVvsBoostHandlers from 'views/xVvs/hooks/useVvsBoostHandlers'
import { getEarningsText } from './EarningsText'

const StyledCard = styled(Card)`
  width: 100%;
  height: fit-content;
`

const lookupPoolsContract = (pools) => {
  return _.mapValues(
    _.keyBy(pools, (pool) => pool.sousId),
    (pool) => useSousChef(pool.sousId),
  )
}

const HarvestCard = () => {
  const [pendingTx, setPendingTx] = useState(false)
  const { t } = useTranslation()
  const { toastSuccess, toastError } = useToast()
  const { farmsWithStakedBalance, earningsSum: farmEarningsSum } = useFarmsWithBalance()
  const { farmsV2WithStakedBalance, earningsUsdc: farmsV2EarningsUsdc } = useFarmsV2WithBalance()

  const { onDeposit } = useClpHandlers()
  const { onDeposit: onVvsBoostDeposit } = useVvsBoostHandlers()

  const craftsmanContract = useCraftsman()
  const craftsmanV2Contract = useCraftsmanV2()
  const vvsPriceUsdc = usePriceVvsUsdc()
  const farmsV1EarningsUsdc = new BigNumber(farmEarningsSum).multipliedBy(vvsPriceUsdc)
  const numFarmsToCollect = farmsWithStakedBalance.filter((value) => value.pid !== 0).length
  const numFarmsV2ToCollect = farmsV2WithStakedBalance.length
  const numTotalToCollect = farmsWithStakedBalance.length + numFarmsV2ToCollect

  const { pools } = usePools()
  const poolContracts = lookupPoolsContract(pools)
  const stakedOnlyMines = useMemo(
    () =>
      pools.filter((pool) => {
        return pool.userData && new BigNumber(pool.userData.stakedBalance).isGreaterThan(0)
      }),
    [pools],
  )
  const numMinesToCollect = stakedOnlyMines.length
  const totalPendingRewardsForMines = stakedOnlyMines
    .filter((mine) => mine.sousId !== 0)
    .map((pool) => getBalanceNumber(pool.userData.pendingReward, pool.earningToken.decimals) * pool.earningTokenPrice)
  const earningUsdcForMines = _.sumBy(totalPendingRewardsForMines)

  const orders = useRemovalOrders()
  const userDepositedRemovalOrders = useMemo(
    () => orders.filter((o) => o.userData && o.userData.stakedBalance.isGreaterThan('0')),
    [orders],
  )
  const numRemovalOrdersToCollect = userDepositedRemovalOrders.length
  const earningUsdcForRemovalOrders = userDepositedRemovalOrders
    .map((r) => r.userData.pendingVvs.multipliedBy(vvsPriceUsdc))
    .reduce((prev, next) => prev.plus(next), BIG_ZERO)

  const { boostData } = useUserVvsBoost()
  const { pendingVvs } = boostData
  const earningUsdcForVvsBoost = getBalanceAmount(pendingVvs, Tokens.vvs.decimals).multipliedBy(vvsPriceUsdc)

  const totalEarningUsdc: BigNumber = farmsV1EarningsUsdc
    .plus(farmsV2EarningsUsdc)
    .plus(earningUsdcForMines)
    .plus(getBalanceAmount(earningUsdcForRemovalOrders))
    .plus(earningUsdcForVvsBoost)

  const earningsText = getEarningsText(
    numFarmsToCollect + numFarmsV2ToCollect + numRemovalOrdersToCollect,
    numMinesToCollect + (earningUsdcForVvsBoost.gt('0') ? 1 : 0),
    t,
  )

  const harvestAllFarmsAndMines = useCallback(async () => {
    setPendingTx(true)
    // eslint-disable-next-line no-restricted-syntax
    for (const farmWithBalance of farmsWithStakedBalance) {
      try {
        // eslint-disable-next-line no-await-in-loop
        await harvestFarm(craftsmanContract, farmWithBalance.pid)
        toastSuccess(
          `${t('Harvested')}!`,
          t('Your %symbol% earnings have been sent to your wallet!', { symbol: 'VVS' }),
        )
      } catch (error) {
        toastError(
          t('Error'),
          t(
            'The network may be congested. Please try again and confirm the transaction. Do not change the default gas transactions settings for Cronos as it will not help to speed up.',
          ),
        )
      }
    }
    // eslint-disable-next-line no-restricted-syntax
    for (const farmV2WithBalance of farmsV2WithStakedBalance) {
      try {
        // eslint-disable-next-line no-await-in-loop
        await harvestFarm(craftsmanV2Contract, farmV2WithBalance.pid)
        toastSuccess(
          `${t('Harvested')}!`,
          t('Your %symbol% earnings have been sent to your wallet!', { symbol: 'multi token' }),
        )
      } catch (error) {
        toastError(
          t('Error'),
          t(
            'The network may be congested. Please try again and confirm the transaction. Do not change the default gas transactions settings for Cronos as it will not help to speed up.',
          ),
        )
      }
    }
    // eslint-disable-next-line no-restricted-syntax
    for (const pool of stakedOnlyMines) {
      try {
        // eslint-disable-next-line no-await-in-loop
        await harvestPool(poolContracts[pool.sousId])
        toastSuccess(
          `${t('Harvested')}!`,
          t('Your %symbol% earnings have been sent to your wallet!', { symbol: pool.earningToken.symbol }),
        )
      } catch (error) {
        toastError(
          t('Error'),
          t(
            'The network may be congested. Please try again and confirm the transaction. Do not change the default gas transactions settings for Cronos as it will not help to speed up.',
          ),
        )
      }
    }
    // eslint-disable-next-line no-restricted-syntax
    for (const order of userDepositedRemovalOrders) {
      try {
        // eslint-disable-next-line no-await-in-loop
        await onDeposit(order.lpAddress, '0')
        toastSuccess(
          `${t('Harvested')}!`,
          t('Your %symbol% earnings have been sent to your wallet!', { symbol: Tokens.vvs.symbol }),
        )
      } catch (error) {
        toastError(
          t('Error'),
          t(
            'The network may be congested. Please try again and confirm the transaction. Do not change the default gas transactions settings for Cronos as it will not help to speed up.',
          ),
        )
      }
    }

    try {
      await onVvsBoostDeposit('0', '0')
      toastSuccess(
        `${t('Harvested')}!`,
        t('Your %symbol% earnings have been sent to your wallet!', { symbol: Tokens.vvs.symbol }),
      )
    } catch (error) {
      toastError(
        t('Error'),
        t(
          'The network may be congested. Please try again and confirm the transaction. Do not change the default gas transactions settings for Cronos as it will not help to speed up.',
        ),
      )
    }

    setPendingTx(false)
  }, [
    farmsWithStakedBalance,
    farmsV2WithStakedBalance,
    craftsmanContract,
    craftsmanV2Contract,
    toastSuccess,
    t,
    toastError,
    stakedOnlyMines,
    poolContracts,
    userDepositedRemovalOrders,
    onDeposit,
    onVvsBoostDeposit,
  ])

  return (
    <StyledCard>
      <CardBody>
        <Flex
          flexDirection={['row', null, null, 'row']}
          justifyContent="space-between"
          alignItems={['flex-start', null, null, 'center']}
        >
          <Flex flexDirection="column" alignItems={['flex-start', null, null, 'flex-start']}>
            {totalEarningUsdc.toNumber() ? (
              <LocalBalance
                decimals={totalEarningUsdc.gt(0) ? 2 : 0}
                fontSize="24px"
                bold
                prefix={totalEarningUsdc.gt(0) ? '~' : ''}
                lineHeight="1.1"
                value={totalEarningUsdc}
              />
            ) : (
              <Skeleton width={96} height={24} my="2px" />
            )}
            <Text small color="textSubtle" fontSize="13px">
              {earningsText}
            </Text>
          </Flex>
          {numTotalToCollect <= 0 ? (
            <Link href="/farms">
              <Button width={['100%', null, null, 'auto']} variant="secondary">
                <Text color="primary" bold fontSize="13px" style={{ whiteSpace: 'nowrap' }}>
                  {t('Start earning')}
                </Text>
              </Button>
            </Link>
          ) : (
            <Button
              width={['auto', null, null, 'auto']}
              id="harvest-all"
              isLoading={pendingTx}
              endIcon={pendingTx ? <AutoRenewIcon spin color="currentColor" /> : null}
              disabled={pendingTx}
              onClick={harvestAllFarmsAndMines}
            >
              <Text color="white" bold fontSize="13px" style={{ whiteSpace: 'nowrap' }}>
                {pendingTx ? t('Harvesting') : t('Harvest all')}
              </Text>
            </Button>
          )}
        </Flex>
      </CardBody>
    </StyledCard>
  )
}

export default HarvestCard
